import { IBrandViewV2Response } from "@faire/web-api/indigofair/data/IBrandViewV2Response";
import { IFilterOption } from "@faire/web-api/indigofair/data/IFilterOption";
import { useStrictLocalization } from "@faire/web/common/localization";
import { DEPRECATED_backwardsCompatibleValues } from "@faire/web/common/localization/localize";
import { Column, Row } from "@faire/web/slate/Layout";
import { Spacer } from "@faire/web/slate/spacing";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { useViewport } from "@faire/web/ui/hooks/useViewport";
import React, { Suspense, useMemo } from "react";
import { useHistory } from "react-router-dom";
import styled from "styled-components";

import { Banner } from "@faire/retailer/app/visitor/[locale]/(pagesWithModal)/(defaultHeader)/brand/[brandToken]/(rsc)/rsc/_sections/Banner/Banner";
import { Info } from "@faire/retailer/app/visitor/[locale]/(pagesWithModal)/(defaultHeader)/brand/[brandToken]/(rsc)/rsc/_sections/Info/Info";
import { Story } from "@faire/retailer/app/visitor/[locale]/(pagesWithModal)/(defaultHeader)/brand/[brandToken]/(rsc)/rsc/_sections/Info/Story/Story";
import {
  INFO_MARGIN_RIGHT_WIDTH,
  INFO_WIDTH,
} from "@faire/retailer/app/visitor/[locale]/(pagesWithModal)/(defaultHeader)/brand/[brandToken]/(rsc)/rsc/_sections/Info/styles.css";
import { BRAND_PRODUCTS_SECTION_ID } from "@faire/retailer/app/visitor/[locale]/(pagesWithModal)/(defaultHeader)/brand/[brandToken]/(rsc)/rsc/BRAND_PRODUCTS_SECTION_ID";
import { LazyBrandProducts } from "@faire/retailer/components/LoggedOutBrand/brand-products";
import { FilterTabs } from "@faire/retailer/components/LoggedOutBrand/FilterTabs";
import { LoadingProductTile } from "@faire/retailer/components/LoggedOutBrand/LoadingProductTile";
import { ILeanProductTile } from "@faire/retailer/features/MarketplaceItems/MarketplaceProduct/LeanProtos/ILeanProductTile";
import { buildBrandReviewSummaryFromBrandReviews } from "@faire/retailer/lib/buildReviewSummary";
import type { IReviewSummary } from "@faire/retailer/lib/buildReviewSummary";
import { useTopOfPageContentStore } from "@faire/retailer/stores/ui/TopOfPageContentStore";

import { FaireDirectBrandInfo } from "./__internal__/FaireDirectBrandInfo/FaireDirectBrandInfo";
import { FaireDirectFilterTabs } from "./__internal__/FaireDirectFilterTabs";

export interface IBrandProfileSummaryProps {
  brand: IBrandViewV2Response.ILeanBrand;
  productTiles?: ILeanProductTile[];
  productTilesIsLoading?: boolean;
  productsRequestId?: string;
  totalProductsCount: number | undefined;
  showVerticalImagery?: boolean;
  loadTimingRef?: React.Ref<HTMLDivElement>;
  brandAboutSubheader: string | null;
  categoryFilterOptions: IFilterOption[] | null;
  isFaireDirectLandingPage?: boolean;
  numberOfProductsToShow?: number;
}

export const BrandProfileSummary: React.FC<IBrandProfileSummaryProps> = ({
  brand,
  productTiles,
  productTilesIsLoading,
  productsRequestId,
  totalProductsCount,
  showVerticalImagery,
  loadTimingRef,
  brandAboutSubheader,
  categoryFilterOptions,
  isFaireDirectLandingPage,
  numberOfProductsToShow,
}) => {
  const { isTabletAndAbove } = useViewport();
  const { strictLocalize } = useStrictLocalization();

  const { based_in_city: city, based_in_state: state } = brand;
  const location =
    city || state
      ? strictLocalize(
          {
            defaultMessage: "Based in {location}",
            description: {
              text: "Location where the brand is based",
            },
          },
          DEPRECATED_backwardsCompatibleValues({
            location: [city, state].filter((el) => el?.length).join(", "),
          })
        )
      : undefined;

  const brandReviewSummary = useMemo(
    () => buildBrandReviewSummaryFromBrandReviews(brand.brand_reviews_summary),
    [brand.brand_reviews_summary]
  );

  const { heightWithoutPromoBannerExceptMarket: topOfPageHeight } =
    useTopOfPageContentStore(["heightWithoutPromoBannerExceptMarket"]);

  return (
    <>
      <Banner
        banner_image={brand.banner_image}
        profile_image={brand.profile_image}
        $ref={loadTimingRef}
      />

      <Spacer height={{ tabletAndAbove: "8x", mobileAndAbove: "7x" }} />

      <StyledRow>
        {isTabletAndAbove ? (
          <>
            <RegularOrFaireDirectBrandInfo
              isFaireDirectLandingPage={isFaireDirectLandingPage}
              brand={brand}
              brandReviewSummary={brandReviewSummary}
              brandAboutSubheader={brandAboutSubheader}
            />
            <ContentWrapper>
              <RegularOrFaireDirectFilterTabs
                brandToken={brand.token}
                categoryFilterOptions={categoryFilterOptions}
                isFaireDirectLandingPage={isFaireDirectLandingPage}
              />
              {productTilesIsLoading ? (
                <LoadingProductTile />
              ) : (
                <Suspense fallback={<LoadingProductTile />}>
                  <LazyBrandProducts
                    rsc={false}
                    productTiles={productTiles ?? []}
                    brandName={brand.name}
                    brandToken={brand.token}
                    productsRequestId={productsRequestId}
                    totalProductCount={totalProductsCount}
                    showVerticalImagery={showVerticalImagery}
                    productsContainerRef={loadTimingRef}
                    productsPageSize={numberOfProductsToShow}
                  />
                </Suspense>
              )}
            </ContentWrapper>
          </>
        ) : (
          <StyledColumn>
            <RegularOrFaireDirectBrandInfo
              isFaireDirectLandingPage={isFaireDirectLandingPage}
              brand={brand}
              brandReviewSummary={brandReviewSummary}
              brandAboutSubheader={brandAboutSubheader}
            />
            <div
              style={{
                width: "100%",
                scrollMarginTop: topOfPageHeight,
              }}
              id={BRAND_PRODUCTS_SECTION_ID}
            >
              <RegularOrFaireDirectFilterTabs
                brandToken={brand.token}
                categoryFilterOptions={categoryFilterOptions}
                isFaireDirectLandingPage={isFaireDirectLandingPage}
              />
              <StyledColumn>
                {productTilesIsLoading ? (
                  <LoadingProductTile isMobile />
                ) : (
                  <>
                    <Suspense fallback={<LoadingProductTile isMobile />}>
                      <LazyBrandProducts
                        rsc={false}
                        productTiles={productTiles ?? []}
                        brandName={brand.name}
                        brandToken={brand.token}
                        productsRequestId={productsRequestId}
                        totalProductCount={totalProductsCount}
                        showVerticalImagery={showVerticalImagery}
                        productsContainerRef={loadTimingRef}
                        productsPageSize={numberOfProductsToShow}
                      />
                    </Suspense>
                  </>
                )}
              </StyledColumn>
            </div>
            {isFaireDirectLandingPage ? null : (
              <Story
                brand={brand}
                reviewSummary={brandReviewSummary}
                location={location}
                brandAboutSubheader={brandAboutSubheader}
              />
            )}
          </StyledColumn>
        )}
      </StyledRow>
    </>
  );
};

const RegularOrFaireDirectBrandInfo: React.FC<{
  brand: IBrandViewV2Response.ILeanBrand;
  brandReviewSummary: IReviewSummary | undefined;
  brandAboutSubheader: string | null;
  isFaireDirectLandingPage?: boolean;
}> = ({
  isFaireDirectLandingPage,
  brand,
  brandReviewSummary,
  brandAboutSubheader,
}) => {
  if (isFaireDirectLandingPage) {
    return <FaireDirectBrandInfo brand={brand} />;
  }

  return (
    <Info
      brand={brand}
      reviewSummary={brandReviewSummary}
      brandAboutSubheader={brandAboutSubheader}
    />
  );
};

const RegularOrFaireDirectFilterTabs: React.FC<{
  isFaireDirectLandingPage?: boolean;
  categoryFilterOptions: IFilterOption[] | null;
  brandToken: string | undefined;
}> = ({ isFaireDirectLandingPage, categoryFilterOptions, brandToken }) => {
  const { push } = useHistory();

  if (isFaireDirectLandingPage) {
    return (
      <FaireDirectFilterTabs
        brandToken={brandToken}
        categoryFilterOptions={categoryFilterOptions}
        locationPush={push}
      />
    );
  }

  return (
    <FilterTabs
      brandToken={brandToken}
      categoryFilterOptions={categoryFilterOptions}
      locationPush={push}
      scrollIntoViewOnClickMobileElementId={BRAND_PRODUCTS_SECTION_ID}
    />
  );
};

const StyledRow = styled(Row).attrs({ align: "stretch" })`
  margin: 0 16px;

  ${responsiveMediaQueries.tabletAndAbove} {
    margin: 0 56px;
  }
`;

const StyledColumn = styled(Column).attrs({ align: "center" })`
  width: 100%;
`;

const ContentWrapper = styled(Column)`
  width: calc(100% - ${INFO_WIDTH.desktopAndAbove + INFO_MARGIN_RIGHT_WIDTH}px);

  ${responsiveMediaQueries.tablet} {
    width: calc(100% - ${INFO_WIDTH.tablet + INFO_MARGIN_RIGHT_WIDTH}px);
  }

  ${responsiveMediaQueries.mobile} {
    width: 100%;
  }
`;
